export enum REDO_OUTBOUND_CARRIER_IDS {
  FEDEX_SMARTPOST = "ca_f996de1247154d2b84c4e51f3df913f6",
  FEDEX = "ca_19dd1198bb3d462ea25612b34f04d6a7",
  EASYPOST_USPS = "ca_fa25281c95a445939738a4725bd9c2b5",
  B2B_UPS = "ca_4011dee962d44d3db1d05d9aea5549a3",
  B2C_UPS = "ca_1ae50b34cac648a2a12f8978ce65b049",
  USPS_SHIP = "ca_58a3433030904f4f86c1eb33a6a1aa47", // Note: Sometimes we have problems manifesting USPS Ship - see explanation here: https://github.com/redoapp/redo/pull/16662
  AMAZON = "ca_beb146dd1f7944f3b9fb6d564a615f96",
  UPS_DYNAMIC = "ca_b22b08164cb14a02bea564b84208d11d",
  UPS_GROUND_SAVER_DYNAMIC = "ca_aa54b60b48a54be79e90eb6cb5154447",
  RETURN_BEAR_AUSTRALIA_POST = "ca_76d6ebe306dd453cb3fc56fb0a471d50",
  EPOST_GLOBAL = "ca_7c85df2facf246bf8aef41c24c002383",
  EPOST_GLOBAL_LCG = "ca_d7a53fadeb274785a1d874195a1ce8bb",
  APC = "ca_3bfef8e67a5649d18bc4b064e6c8b5df",
  APC_LCG = "ca_a91ef57dbb8b4842afca7a991c51a767",
  DHL_EXPRESS = "ca_3114746462194996847af7716c6ee58b",
  DHL_EXPRESS_LCG = "ca_4fcda3d7a7c545eda9baa66a18d92cf6",
}

const LCG_TO_DEFAULT_CARRIER_ACCOUNTS: ReadonlyMap<string, string> = new Map([
  [
    REDO_OUTBOUND_CARRIER_IDS.EPOST_GLOBAL_LCG,
    REDO_OUTBOUND_CARRIER_IDS.EPOST_GLOBAL,
  ],
  [REDO_OUTBOUND_CARRIER_IDS.APC_LCG, REDO_OUTBOUND_CARRIER_IDS.APC],
]);

export function nonLcgCarrierAccountId(carrierAccountId: string): string {
  return (
    LCG_TO_DEFAULT_CARRIER_ACCOUNTS.get(carrierAccountId) ?? carrierAccountId
  );
}

/**
 * A weight-based upcharge configured on an individual carrier account, in
 * dollars per pound. The amount is added to a rate's `provider_cost` before
 * spreads are applied, so the carrier margin is calculated on the
 * upcharge-inclusive cost. It is proportional to weight and is NOT rounded up
 * to whole pounds (e.g. 1 lb 6 oz = 1.375 lb).
 *
 * Use this for real per-shipment carrier costs that the rate provider does not
 * fold into its quoted `provider_cost`. `reason` documents why the carrier
 * needs it and the review cadence for the amount.
 */
export interface CarrierWeightUpcharge {
  perPound: number;
  reason: string;
}

const OUNCES_PER_POUND = 16;

const EPOST_GLOBAL_FUEL_SURCHARGE: CarrierWeightUpcharge = {
  perPound: 0.44,
  reason:
    "EPost Global fuel surcharge — EasyPost does not fold it into the quoted " +
    "provider_cost. Reviewed monthly; update perPound when EPost Global " +
    "publishes a new value.",
};

/**
 * Per-carrier-account weight upcharges. Keyed by carrier account id so the rate
 * pipeline stays carrier-agnostic — to add an upcharge for another carrier, add
 * an entry here rather than branching on carrier in the rate code.
 */
export const CARRIER_WEIGHT_UPCHARGES: ReadonlyMap<
  string,
  CarrierWeightUpcharge
> = new Map([
  [REDO_OUTBOUND_CARRIER_IDS.EPOST_GLOBAL, EPOST_GLOBAL_FUEL_SURCHARGE],
  [REDO_OUTBOUND_CARRIER_IDS.EPOST_GLOBAL_LCG, EPOST_GLOBAL_FUEL_SURCHARGE],
]);

/**
 * Dollar amount of the configured weight upcharge for a carrier account at a
 * given parcel weight (in ounces). Returns 0 when the carrier has no configured
 * upcharge.
 */
export function carrierWeightUpchargeAmount(
  carrierAccountId: string,
  weightOz: number,
): number {
  const upcharge = CARRIER_WEIGHT_UPCHARGES.get(carrierAccountId);
  if (!upcharge) {
    return 0;
  }
  return (weightOz / OUNCES_PER_POUND) * upcharge.perPound;
}

export const DEFAULT_CARRIER_ACCOUNTS = [
  REDO_OUTBOUND_CARRIER_IDS.FEDEX_SMARTPOST,
  REDO_OUTBOUND_CARRIER_IDS.FEDEX,
  REDO_OUTBOUND_CARRIER_IDS.B2B_UPS,
  REDO_OUTBOUND_CARRIER_IDS.B2C_UPS,
  REDO_OUTBOUND_CARRIER_IDS.UPS_DYNAMIC,
  REDO_OUTBOUND_CARRIER_IDS.UPS_GROUND_SAVER_DYNAMIC,
  // This should ONLY be used for getting USPS letter rates
  REDO_OUTBOUND_CARRIER_IDS.EASYPOST_USPS,
] as readonly REDO_OUTBOUND_CARRIER_IDS[];
